import React, { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Loader2, Paperclip } from 'lucide-react';
import { api } from '../../api/client';
import { invalidateTickets } from '../../lib/tickets-invalidation';
import { todayCalendarDate } from '../../lib/calendar-date';
import { STAMP_SIGNER_NAME_MAX_LENGTH } from '../../lib/approval-stamp';

/**
 * The office attaches a copy of the ticket the customer signed outside FieldIQ
 * — stamped in Acrobat or Bluebeam and emailed back (api#995).
 *
 * TWO SHAPES, decided by the status the page is showing:
 *
 *   office_approved   the attach RECORDS the approval: "Approved by" is
 *                     required and "Approved on" defaults to the tenant's
 *                     today. The API moves the ticket to customer_approved.
 *   customer_approved the approval is already recorded and the API refuses a
 *   / invoiced        name or date with a 400 rather than overwrite it, so the
 *                     dialog asks for the file alone.
 *
 * The status itself is never set here; it comes back on the refetch.
 *
 * API REFUSALS ARE SHOWN VERBATIM — 409 (already holds a stamp or a file, or
 * the status moved), 413, 415, 429 — in the dialog, where the file input is.
 * The fallbacks below only cover a refusal with no JSON body, such as a 413
 * from an edge proxy that never reached the route.
 */
const FALLBACK_BY_STATUS: Record<number, string> = {
  413: 'File is larger than 20 MB.',
  415: 'Upload a PDF, PNG or JPEG file.',
  429: 'Too many uploads. Please wait a few minutes and try again.',
};

interface Props {
  ticketId: string;
  ticketNumber: string | null;
  status: string;
  timeZone: string | null;
  onClose: () => void;
  onAttached: (message: string) => void;
}

const FIELD =
  'w-full rounded-fi-control border border-fieldiq-border bg-fieldiq-dark px-3 py-2.5 text-sm text-white focus:border-fi-amber focus:outline-none';

export const AttachSignedCopyDialog: React.FC<Props> = ({
  ticketId, ticketNumber, status, timeZone, onClose, onAttached,
}) => {
  const queryClient = useQueryClient();
  const recordsApproval = status === 'office_approved';
  const today = todayCalendarDate(timeZone);
  const [file, setFile] = useState<File | null>(null);
  const [approvedBy, setApprovedBy] = useState('');
  const [approvedOn, setApprovedOn] = useState(today ?? '');
  const [error, setError] = useState<string | null>(null);

  const attach = useMutation({
    mutationFn: () =>
      api.tickets.attachCustomerSignedCopy(ticketId, {
        file: file!,
        ...(recordsApproval ? { approvedByName: approvedBy.trim(), approvedOn: approvedOn || undefined } : {}),
      }),
    onSuccess: () => {
      invalidateTickets(queryClient);
      onAttached(
        recordsApproval
          ? `Signed copy attached — customer approval recorded for ${approvedBy.trim()}`
          : 'Signed copy attached',
      );
    },
    onError: (err: any) => {
      const status: number | undefined = err?.response?.status;
      const fromApi = err?.response?.data?.error;
      setError(
        (typeof fromApi === 'string' && fromApi) ||
          (status && FALLBACK_BY_STATUS[status]) ||
          err?.userMessage ||
          'Failed to attach the signed copy',
      );
      // A 409 can mean the ticket changed under the page; refetch so what is
      // behind the dialog is current when it closes.
      if (status === 409) invalidateTickets(queryClient);
    },
  });

  const ready = !!file && (!recordsApproval || approvedBy.trim() !== '') && !attach.isPending;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-labelledby="attach-signed-copy-title"
      data-testid="attach-signed-copy-dialog"
    >
      <form
        className="w-full max-w-md animate-fade-in rounded-fi-panel border border-fieldiq-border bg-fieldiq-card p-5 shadow-2xl"
        onSubmit={(e) => { e.preventDefault(); if (ready) { setError(null); attach.mutate(); } }}
      >
        <h3 id="attach-signed-copy-title" className="mb-1 text-lg font-bold text-white">
          Attach customer-signed copy
        </h3>
        <p className="mb-4 text-sm text-fieldiq-text">
          {recordsApproval
            ? `The copy of ${ticketNumber || 'this ticket'} the customer signed and sent back. Attaching it records their approval.`
            : `The copy of ${ticketNumber || 'this ticket'} the customer signed. The approval already recorded on the ticket is kept as it is.`}
        </p>

        <label className="mb-1 block text-xs font-medium text-fieldiq-text" htmlFor="signed-copy-file">
          Signed copy (PDF, PNG or JPEG, up to 20 MB)
        </label>
        <input
          id="signed-copy-file"
          type="file"
          accept="application/pdf,image/png,image/jpeg"
          onChange={(e) => { setFile(e.target.files?.[0] ?? null); setError(null); }}
          className={`${FIELD} mb-4 file:mr-3 file:rounded file:border file:border-fieldiq-border file:bg-fieldiq-card file:px-2 file:py-1 file:text-xs file:text-fieldiq-text`}
        />

        {recordsApproval && (
          <>
            <label className="mb-1 block text-xs font-medium text-fieldiq-text" htmlFor="signed-copy-approved-by">
              Approved by
            </label>
            <input
              id="signed-copy-approved-by"
              type="text"
              required
              maxLength={STAMP_SIGNER_NAME_MAX_LENGTH}
              value={approvedBy}
              onChange={(e) => setApprovedBy(e.target.value)}
              placeholder="Name of the customer who signed"
              className={`${FIELD} mb-4`}
            />
            <label className="mb-1 block text-xs font-medium text-fieldiq-text" htmlFor="signed-copy-approved-on">
              Approved on
            </label>
            <input
              id="signed-copy-approved-on"
              type="date"
              value={approvedOn}
              max={today ?? undefined}
              onChange={(e) => setApprovedOn(e.target.value)}
              className={`${FIELD} mb-4`}
            />
          </>
        )}

        {error && (
          <p role="alert" className="mb-4 rounded-fi-control border border-fieldiq-danger/40 bg-fieldiq-danger/10 px-3 py-2 text-sm text-fieldiq-text" data-testid="attach-signed-copy-error">
            {error}
          </p>
        )}

        <div className="flex gap-3">
          <button
            type="button"
            onClick={onClose}
            disabled={attach.isPending}
            className="flex-1 rounded-fi-control border border-fieldiq-border bg-fieldiq-dark py-2.5 text-sm font-medium text-white"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={!ready}
            className="fi-action fi-action--standard fi-action--primary flex flex-1 items-center justify-center gap-2 disabled:opacity-50"
          >
            {attach.isPending ? <Loader2 size={14} className="animate-spin" /> : <Paperclip size={14} />}
            {attach.isPending ? 'Attaching…' : 'Attach'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AttachSignedCopyDialog;
